import { useEffect } from "react";
import { X, AlertTriangle, CheckCircle2, Clock, GripVertical } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SearchableSelect } from "@/components/SearchableSelect";

// ---------- Sortable service row (drag & drop reorder in booking) ----------
function SortableServiceRow({ sortId, idx, it, svc, eligible, primaryStaff, staff, setItem, removeItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`appt-service-row-${idx}`}
         className="rounded-xl border border-[color:var(--sp-border)] bg-white p-3 space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" {...attributes} {...listeners}
                data-testid={`appt-service-drag-${idx}`}
                className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-text)] cursor-grab active:cursor-grabbing"
                title="Trascina per riordinare">
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black text-white flex-shrink-0"
              style={{ background: svc.color || "#06B6D4" }}>
          {idx + 1}. {svc.name}
        </span>
        <span className="text-[11px] text-[color:var(--sp-text-muted)]">
          {svc.duration_min}min · €{svc.price}
        </span>
        <button type="button" onClick={() => removeItem(idx)}
                data-testid={`appt-service-remove-${idx}`}
                className="ml-auto p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-[1fr_130px] gap-2">
        <div>
          <label className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider">Operatore</label>
          <SearchableSelect
            testId={`appt-service-staff-${idx}`}
            value={it.staff_id || ""}
            onChange={(v) => setItem(idx, { staff_id: v || null })}
            options={[
              { id: "", label: "Default appuntamento", sub: primaryStaff ? staff.find((s) => s.id === primaryStaff)?.name : "" },
              ...eligible.map((s) => ({ id: s.id, label: s.name, color: s.color })),
            ]}
            placeholder="Default appuntamento"
          />
          {it.staff_id && (() => {
            const st = staff.find((m) => m.id === it.staff_id);
            const cant = st && st.service_ids?.length && !st.service_ids.includes(it.service_id);
            return cant ? (
              <div className="mt-1 text-[10px] text-amber-700 flex items-center gap-1" data-testid={`staff-cant-do-warning-${idx}`}>
                <AlertTriangle className="h-3 w-3" /> {st.name} non è configurato per {svc.name}
              </div>
            ) : null;
          })()}
        </div>
        <div>
          <label className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider inline-flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" /> Pulizia
          </label>
          <div className="flex items-center gap-1 mt-1">
            <input
              type="number" min="0" step="5"
              data-testid={`appt-service-buffer-${idx}`}
              value={it.buffer_after_min || 0}
              onChange={(e) => setItem(idx, { buffer_after_min: Math.max(0, Number(e.target.value) || 0) })}
              className="sp-input !py-1.5 text-sm w-full"
            />
            <span className="text-[11px] text-[color:var(--sp-text-muted)]">min</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ApptModal({ onClose, onSubmit, onDelete, onComplete, form, setForm, clients, services, staff, editing, openHours, dayCfgFor, isAvailable }) {
  const sortSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  // Global Esc to close
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const setItem = (idx, patch) => {
    const next = form.service_items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    setForm({ ...form, service_items: next });
  };
  const addItem = (sid) => {
    if (!sid) return;
    setForm({ ...form, service_items: [...form.service_items, { service_id: sid, staff_id: null, buffer_after_min: 0 }] });
  };
  const removeItem = (idx) => {
    setForm({ ...form, service_items: form.service_items.filter((_, i) => i !== idx) });
  };
  const autoEnd = () => {
    let dur = 0;
    form.service_items.forEach((it) => {
      const s = services.find((x) => x.id === it.service_id);
      if (s) dur += (s.duration_min || 0);
      dur += Number(it.buffer_after_min || 0);
    });
    if (!dur) return;
    const [h, m] = form.start_time.split(":").map(Number);
    const total = h * 60 + m + dur;
    const eh = String(Math.floor(total / 60) % 24).padStart(2, "0");
    const em = String(total % 60).padStart(2, "0");
    setForm({ ...form, end_time: `${eh}:${em}` });
  };
  const dayCfg = dayCfgFor(form.start_date);

  // Check if start time is outside working hours
  const [sh, sm] = (form.start_time || "0:0").split(":").map(Number);
  const primaryStaff = form.staff_id || form.service_items.find((it) => it.staff_id)?.staff_id || "";
  const outsideHours = form.start_date && primaryStaff
    ? !isAvailable(form.start_date, sh, sm, primaryStaff)
    : (dayCfg.closed || false);

  // Total minutes helper (used in the header hint)
  const totalMinutes = form.service_items.reduce((acc, it) => {
    const s = services.find((x) => x.id === it.service_id);
    return acc + (s?.duration_min || 0) + Number(it.buffer_after_min || 0);
  }, 0);

  const serviceIdsInForm = form.service_items.map((it) => it.service_id);

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} onKeyDown={(e) => e.key === "Escape" && onClose()}>
      <div className="sp-card w-full max-w-2xl p-5 sm:p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl font-black">{editing ? "Modifica appuntamento" : "Nuovo appuntamento"}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-bold">Cliente</label>
            <div className="mt-1">
              <SearchableSelect
                testId="appt-client-select"
                required
                value={form.client_id}
                onChange={(v) => setForm({ ...form, client_id: v })}
                options={clients.map((c) => ({ id: c.id, label: c.name, sub: c.phone || c.email || "" }))}
                placeholder="Seleziona…"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold">Servizi & Operatore</label>
              {totalMinutes > 0 && (
                <button type="button" onClick={autoEnd} className="text-xs text-[color:var(--sp-brand)] font-bold hover:underline">
                  Calcola fine ({totalMinutes} min tot.)
                </button>
              )}
            </div>

            {form.service_items.length > 0 && (
              <div className="mt-2 space-y-2" data-testid="appt-service-items">
                <DndContext
                  sensors={sortSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => {
                    const { active, over } = e;
                    if (!over || active.id === over.id) return;
                    const oldIdx = form.service_items.findIndex((_, i) => `svc-item-${i}` === active.id);
                    const newIdx = form.service_items.findIndex((_, i) => `svc-item-${i}` === over.id);
                    if (oldIdx < 0 || newIdx < 0) return;
                    setForm({ ...form, service_items: arrayMove(form.service_items, oldIdx, newIdx) });
                  }}
                >
                  <SortableContext items={form.service_items.map((_, i) => `svc-item-${i}`)} strategy={verticalListSortingStrategy}>
                    {form.service_items.map((it, idx) => {
                      const svc = services.find((x) => x.id === it.service_id);
                      if (!svc) return null;
                      const eligible = staff.filter((m) => !m.service_ids?.length || m.service_ids.includes(it.service_id));
                      return (
                        <SortableServiceRow key={`svc-item-${idx}`} sortId={`svc-item-${idx}`} idx={idx} it={it} svc={svc} eligible={eligible}
                          primaryStaff={primaryStaff} staff={staff} setItem={setItem} removeItem={removeItem} />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </div>
            )}

            <div className="mt-2">
              <SearchableSelect
                testId="appt-service-add"
                value=""
                onChange={(v) => addItem(v)}
                options={services
                  .map((s) => ({ id: s.id, label: s.name, sub: `${s.duration_min}min · €${s.price}` }))}
                placeholder={form.service_items.length ? "+ Aggiungi altro servizio…" : "Seleziona servizio…"}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-bold">Operatore predefinito <span className="text-[10px] text-[color:var(--sp-text-muted)] font-normal">(fallback per righe senza operatore)</span></label>
            <div className="mt-1">
              <SearchableSelect
                testId="appt-staff-select"
                value={form.staff_id}
                onChange={(v) => setForm({ ...form, staff_id: v })}
                options={(() => {
                  const filtered = serviceIdsInForm.length
                    ? staff.filter((m) => !m.service_ids?.length || serviceIdsInForm.every((sid) => m.service_ids.includes(sid)))
                    : staff;
                  return filtered.map((s) => ({ id: s.id, label: s.name, color: s.color }));
                })()}
                placeholder="Seleziona…"
              />
            </div>
            {serviceIdsInForm.length > 0 && form.staff_id && (() => {
              const st = staff.find((m) => m.id === form.staff_id);
              const cant = st && st.service_ids?.length && !serviceIdsInForm.every((sid) => st.service_ids.includes(sid));
              if (!cant) return null;
              return (
                <div data-testid="staff-cant-do-warning" className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <div><b>{st.name}</b> non può eseguire uno dei servizi selezionati. Assegna un operatore per riga o scegli un default compatibile.</div>
                </div>
              );
            })()}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-sm font-bold">Data</label><input type="date" lang="it-IT" required className="sp-input mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><label className="text-sm font-bold">Inizio</label><input data-testid="appt-start-input" type="time" lang="it-IT" required className="sp-input mt-1" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
            <div><label className="text-sm font-bold">Fine</label><input data-testid="appt-end-input" type="time" lang="it-IT" required className="sp-input mt-1" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
          </div>
          {dayCfg && (
            <div className={`text-xs ${dayCfg._override ? "text-[color:var(--sp-brand)] font-bold" : "text-[color:var(--sp-text-muted)]"}`} data-testid="appt-day-cfg">
              {dayCfg._override && "★ Straordinario · "}
              {dayCfg.closed ? "⚠️ Salone chiuso in questa data" : (
                <>Orari: {dayCfg.open}–{dayCfg.close}{dayCfg.open2 ? ` · ${dayCfg.open2}–${dayCfg.close2}` : ""}</>
              )}
            </div>
          )}
          {outsideHours && (
            <div data-testid="outside-hours-warning" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">Appuntamento fuori orario</div>
                <div className="opacity-80">Questo orario è fuori dagli orari di apertura del salone o dal turno del collaboratore. Sarà chiesta conferma al salvataggio.</div>
              </div>
            </div>
          )}
          <div><label className="text-sm font-bold">Stato</label>
            <select className="sp-input mt-1" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="scheduled">Prenotato</option><option value="completed">Completato</option>
              <option value="cancelled">Cancellato</option><option value="no_show">Non presentato</option>
            </select>
          </div>
          <div><label className="text-sm font-bold">Note</label><input className="sp-input mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex gap-2 pt-2">
            {editing && onDelete && <button type="button" onClick={onDelete} className="sp-btn-ghost !py-2 !px-4 text-sm !text-[color:var(--sp-danger)] !border-[color:var(--sp-danger)]/50">Elimina</button>}
            {editing && onComplete && form.status !== "completed" && (
              <button
                type="button"
                data-testid="appt-complete-btn"
                onClick={onComplete}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-[color:var(--sp-success)] text-white hover:opacity-90"
              >
                <CheckCircle2 className="h-4 w-4" /> Completa e incassa
              </button>
            )}
            <button data-testid="appt-submit-btn" className="sp-btn-primary !py-2 !px-4 text-sm flex-1">{editing ? "Salva modifiche" : "Crea appuntamento"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
